<template>
  <PageChrome>
    <template #title>
      <span class="eyebrow">Versions</span>
    </template>

    <template #actions>
      <router-link :to="`/sales-plays/${playId}`" class="backLink">
        <AppIcon name="arrow_right" :size="14" class="backArrow" />
        Back to play
      </router-link>
    </template>

    <div class="playVersions" v-if="playId">
      <div class="panel">
        <div v-if="versionsLoading" class="muted">Loading versions…</div>
        <div v-else-if="versionsError" class="error">{{ versionsError }}</div>
        <div v-else-if="versions.length === 0" class="muted">No version history yet.</div>
        <div v-else class="versionList">
          <div v-for="v in versions" :key="String(v.version)" class="versionRow">
            <div class="versionIdentity">
              <div class="versionTopRow">
                <div class="versionLabel">v{{ v.version }}</div>
                <span v-if="isCurrentVersion(v)" class="n-pill versionCurrentPill">current</span>
                <span class="n-pill">{{ sourceLabel(v.change_source) }}</span>
                <span class="n-pill">{{ versionActorLabel(v) }}</span>
              </div>
              <div class="muted versionMeta">{{ formatTs(v.created_at) }}</div>
            </div>
            <div class="versionBadges">
              <span
                v-for="badge in changeBadgesFor(v)"
                :key="`${badge.key}-${badge.kind}`"
                class="versionChangeBadge"
                :class="`versionChangeBadge--${badge.key}`"
                :title="badge.kind !== 'flag' ? CHANGE_KIND_LABEL[badge.kind] : undefined"
              >
                {{ badge.name }}
                <span v-if="badge.kind !== 'flag'" class="versionChangeKindIcon">
                  <AppIcon :name="CHANGE_KIND_ICON[badge.kind]" :size="11" />
                  <span v-if="badge.count && badge.count > 1" class="versionChangeKindCount">{{ badge.count }}</span>
                </span>
              </span>
            </div>
            <div v-if="previousVersionFor(v)" class="versionActions">
              <button
                class="btn secondary"
                type="button"
                @click="openVersionDiff(previousVersionFor(v), Number(v.version))"
              >
                Diff vs previous
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal
      v-if="versionDiffModal.open"
      :title="`Version diff · v${versionDiffModal.fromVersion} → v${versionDiffModal.toVersion}`"
      :max-width="1100"
      @close="closeVersionDiff"
    >
      <div v-if="versionDiffLoading" class="muted">Loading diff…</div>
      <div v-else-if="!hasAnyDiff" class="muted">No differences.</div>
      <div v-else class="diffBody">
        <div v-if="versionDiffModal.diff?.config_changes?.length" class="diffSection">
          <div class="diffSectionTitle">Settings</div>
          <div class="diffKeyList">
            <div v-for="(ch, idx) in versionDiffModal.diff.config_changes" :key="`cfg-${idx}`" class="diffKeyRow">
              <div class="diffKeyPath">{{ ch.path }}</div>
              <SalesPlayVersionDiffValue :before="ch.before" :after="ch.after" />
            </div>
          </div>
        </div>

        <div v-for="section in subobjectSections" :key="section.key" class="diffSection">
          <template v-if="section.items.length">
            <div class="diffSectionTitle">{{ section.title }}</div>
            <div class="diffKeyList">
              <div v-for="item in section.items" :key="item.id" class="diffKeyRow">
                <div class="diffKeyPath">
                  {{ item.status === 'reordered' ? section.title : (item.label || section.singular) }}
                  <span class="diffSubobjectStatus" :class="`status-${item.status}`">{{ item.status }}</span>
                  <span v-if="item.status === 'changed'" class="muted">v{{ item.before_version }} → v{{ item.after_version }}</span>
                  <span v-else-if="item.status === 'added'" class="muted">v{{ item.after_version }}</span>
                  <span v-else-if="item.status === 'removed'" class="muted">v{{ item.before_version }}</span>
                </div>
                <SalesPlayVersionDiffValue
                  v-if="item.status === 'reordered'"
                  :before="(item.before_order || []).join('\n')"
                  :after="(item.after_order || []).join('\n')"
                />
                <div v-else-if="!item.changes.length" class="muted">No field-level changes.</div>
                <div v-else class="diffKeyList">
                  <div v-for="(ch, idx) in item.changes" :key="`${item.id}-${idx}`" class="diffFieldRow">
                    <div class="diffKeyPath">{{ ch.path }}</div>
                    <SalesPlayVersionDiffValue :before="ch.before" :after="ch.after" />
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
      <template #footer>
        <button class="btn secondary" @click="closeVersionDiff">Close</button>
      </template>
    </Modal>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import SalesPlayVersionDiffValue from '../components/SalesPlayVersionDiffValue.vue'
import Modal from '../../components/Modal.vue'
import AppIcon from '../../components/AppIcon.vue'
import { apiFetch } from '../../services/api'

interface SectionChangeSummary {
  added: number
  removed: number
  changed: number
  reordered: boolean
}

interface VersionChangeSummary {
  config_changed: boolean
  sources: SectionChangeSummary | null
  filters: SectionChangeSummary | null
  actions: SectionChangeSummary | null
  prospecting_agent_changed: boolean
  icp_changed: boolean
  personas_changed: boolean
}

interface VersionListItem {
  version: number
  created_at: string
  created_by_user_id: string | null
  created_by_user_email: string | null
  change_source: string
  change_summary: VersionChangeSummary
}

interface DiffItem {
  path: string
  before: unknown
  after: unknown
}

interface SubobjectDiff {
  id: string
  status: 'added' | 'removed' | 'changed' | 'reordered'
  label: string | null
  before_version: number | null
  after_version: number | null
  changes: DiffItem[]
  before_order?: string[]
  after_order?: string[]
}

interface VersionDiff {
  from_version: number
  to_version: number
  config_changes: DiffItem[]
  sources: SubobjectDiff[]
  filters: SubobjectDiff[]
  actions: SubobjectDiff[]
  prospecting_agent: SubobjectDiff | null
  icp: SubobjectDiff | null
}

const route = useRoute()
const playId = computed(() => String(route.params.id || ''))

const versions = ref<VersionListItem[]>([])
const versionsLoading = ref(true)
const versionsError = ref('')

const versionDiffLoading = ref(false)
const versionDiffModal = ref<{ open: boolean; fromVersion: number | null; toVersion: number | null; diff: VersionDiff | null }>({
  open: false,
  fromVersion: null,
  toVersion: null,
  diff: null,
})

const subobjectSections = computed(() => {
  const diff = versionDiffModal.value.diff
  const sections = [
    { key: 'sources', title: 'Find', singular: 'Source', items: diff?.sources || [] },
    { key: 'filters', title: 'Qualify', singular: 'Filter', items: diff?.filters || [] },
    { key: 'actions', title: 'Actions', singular: 'Action', items: diff?.actions || [] },
    { key: 'prospecting_agent', title: 'Prospect', singular: 'Prospecting agent', items: diff?.prospecting_agent ? [diff.prospecting_agent] : [] },
    { key: 'icp', title: 'ICP', singular: 'ICP', items: diff?.icp ? [diff.icp] : [] },
  ]
  return sections
})

const hasAnyDiff = computed(() => {
  const diff = versionDiffModal.value.diff
  if (!diff) return false
  return !!(
    diff.config_changes.length
    || diff.sources.length
    || diff.filters.length
    || diff.actions.length
    || diff.prospecting_agent
    || diff.icp
  )
})

const versionNumberSet = computed(() => new Set((versions.value || []).map((v) => Number(v.version))))
const currentVersion = computed(() => {
  if (!versions.value.length) return 0
  return Math.max(...versions.value.map((v) => Number(v.version)))
})

function isCurrentVersion(v: VersionListItem) {
  return Number(v.version) === currentVersion.value
}

function previousVersionFor(v: VersionListItem) {
  const prev = Number(v.version) - 1
  return prev > 0 && versionNumberSet.value.has(prev) ? prev : null
}

function sourceLabel(source: string) {
  if (source === 'ui') return 'UI'
  if (source === 'chat') return 'Chat'
  if (source === 'migration') return 'Migration'
  return 'Manual'
}

function versionActorLabel(v: VersionListItem) {
  const email = String(v.created_by_user_email || '').trim()
  const fallback = String(v.created_by_user_id || '').trim()
  return email || (fallback ? `User ${fallback.slice(0, 8)}…` : 'Unknown')
}

function formatTs(ts: string) {
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return ts
  }
}

type ChangeKind = 'added' | 'removed' | 'changed' | 'reordered' | 'flag'

interface ChangeBadge {
  key: string
  name: string
  kind: ChangeKind
  count: number | null
}

function sectionBadges(key: string, name: string, section: SectionChangeSummary | null): ChangeBadge[] {
  if (!section) return []
  const badges: ChangeBadge[] = []
  if (section.added) badges.push({ key, name, kind: 'added', count: section.added })
  if (section.removed) badges.push({ key, name, kind: 'removed', count: section.removed })
  if (section.changed) badges.push({ key, name, kind: 'changed', count: section.changed })
  if (section.reordered) badges.push({ key, name, kind: 'reordered', count: null })
  return badges
}

function changeBadgesFor(v: VersionListItem): ChangeBadge[] {
  const s = v.change_summary
  if (!s) return []
  const badges: ChangeBadge[] = []
  badges.push(...sectionBadges('sources', 'Find', s.sources))
  badges.push(...sectionBadges('filters', 'Qualify', s.filters))
  if (s.prospecting_agent_changed) badges.push({ key: 'prospecting_agent', name: 'Prospect', kind: 'flag', count: null })
  badges.push(...sectionBadges('actions', 'Actions', s.actions))
  if (s.icp_changed) badges.push({ key: 'icp', name: 'ICP', kind: 'flag', count: null })
  if (s.personas_changed) badges.push({ key: 'personas', name: 'Personas', kind: 'flag', count: null })
  if (s.config_changed) badges.push({ key: 'config', name: 'Settings', kind: 'flag', count: null })
  return badges
}

const CHANGE_KIND_LABEL: Record<ChangeKind, string> = {
  added: 'New',
  removed: 'Removed',
  changed: 'Edited',
  reordered: 'Reordered',
  flag: '',
}

const CHANGE_KIND_ICON: Record<ChangeKind, string> = {
  added: 'plus',
  removed: 'trash',
  changed: 'pencil',
  reordered: 'arrow_path',
  flag: '',
}

async function loadVersions() {
  versionsLoading.value = true
  versionsError.value = ''
  try {
    const resp: any = await apiFetch(`/sales-plays/${encodeURIComponent(playId.value)}/versions`)
    versions.value = Array.isArray(resp?.versions) ? resp.versions : []
  } catch (e: any) {
    versionsError.value = e?.message || 'Failed to load versions'
    versions.value = []
  } finally {
    versionsLoading.value = false
  }
}

async function openVersionDiff(fromVersion: number | null, toVersion: number) {
  const fromV = Number(fromVersion || 0)
  const toV = Number(toVersion || 0)
  if (!playId.value || fromV <= 0 || toV <= 0) return
  versionDiffLoading.value = true
  versionDiffModal.value = { open: true, fromVersion: fromV, toVersion: toV, diff: null }
  try {
    const params = new URLSearchParams({
      from_version: String(fromV),
      to_version: String(toV),
    })
    const resp: any = await apiFetch(`/sales-plays/${encodeURIComponent(playId.value)}/versions_diff?${params.toString()}`)
    versionDiffModal.value = { open: true, fromVersion: fromV, toVersion: toV, diff: resp as VersionDiff }
  } catch (e: any) {
    versionsError.value = e?.message || 'Failed to load version diff'
    versionDiffModal.value = { open: false, fromVersion: null, toVersion: null, diff: null }
  } finally {
    versionDiffLoading.value = false
  }
}

function closeVersionDiff() {
  versionDiffModal.value = { open: false, fromVersion: null, toVersion: null, diff: null }
}

onMounted(loadVersions)
</script>

<style scoped>
.eyebrow {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  line-height: var(--lh-tight);
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

.backLink {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color var(--duration-fast) var(--ease-out);
}
.backLink:hover { color: var(--color-accent); }
.backArrow { transform: rotate(180deg); }

.playVersions {
  display: grid;
  gap: var(--space-4);
}

.panel {
  border: 1px solid var(--color-border-subtle);
  border-radius: 12px;
  padding: var(--space-5);
}

.versionList {
  display: grid;
  gap: var(--space-2);
}

.versionRow {
  border: 1px solid var(--color-border-subtle);
  border-radius: 10px;
  padding: var(--space-4) var(--space-5);
  display: flex;
  align-items: center;
  gap: var(--space-5);
}

.versionIdentity {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  flex-shrink: 0;
  width: 340px;
}

.versionTopRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
}

.versionLabel {
  font-size: var(--fs-md);
  font-weight: var(--fw-semibold);
}

.versionMeta {
  font-size: var(--fs-sm);
  white-space: nowrap;
}

.versionTopRow .n-pill {
  /* .n-pill's shared background falls back to gray (--color-bg-muted is
     undefined) — override to the real beige token here rather than
     changing the shared class site-wide. */
  background: var(--color-bg-subtle);
}

.versionCurrentPill {
  color: var(--color-accent);
}

.versionBadges {
  display: flex;
  flex: 1;
  gap: var(--space-2);
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
}

.versionChangeBadge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  border-radius: var(--radius-sm);
  background: var(--color-bg-subtle);
  color: var(--color-text-secondary);
  padding: 5px 8px 5px 12px;
}

.versionChangeKindIcon {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: var(--color-text-tertiary);
  background: var(--color-bg-surface);
  border-radius: 4px;
  padding: 3px 5px;
}

.versionChangeKindCount {
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  line-height: 1;
}

.versionChangeBadge--sources {
  color: #2563eb;
  background: rgba(37, 99, 235, 0.1);
}

.versionChangeBadge--filters {
  color: #d97706;
  background: rgba(217, 119, 6, 0.1);
}

.versionChangeBadge--prospecting_agent {
  color: #059669;
  background: rgba(5, 150, 105, 0.1);
}

.versionChangeBadge--actions {
  color: #7c3aed;
  background: rgba(124, 58, 237, 0.1);
}

.versionActions {
  display: flex;
  gap: var(--space-2);
  flex-wrap: wrap;
  flex-shrink: 0;
}

.diffBody {
  display: grid;
  gap: var(--space-3);
}

.diffKeyList {
  display: grid;
  gap: var(--space-3);
}

.diffKeyRow {
  border: 1px solid var(--color-border-subtle);
  border-radius: 10px;
  padding: var(--space-3);
}

.diffKeyPath {
  font-weight: var(--fw-semibold);
  font-size: var(--fs-sm);
  margin-bottom: var(--space-2);
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.diffSection {
  display: grid;
  gap: var(--space-2);
}

.diffSectionTitle {
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.diffSubobjectStatus {
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  border-radius: 999px;
  padding: 1px 8px;
  text-transform: capitalize;
}

.diffSubobjectStatus.status-added {
  color: var(--color-success, #1a7f37);
  border: 1px solid currentColor;
}

.diffSubobjectStatus.status-removed {
  color: var(--color-danger, #d1242f);
  border: 1px solid currentColor;
}

.diffSubobjectStatus.status-changed {
  color: var(--color-accent);
  border: 1px solid currentColor;
}

.diffSubobjectStatus.status-reordered {
  color: var(--color-text-tertiary);
  border: 1px solid currentColor;
}


.diffFieldRow {
  padding-left: var(--space-3);
  border-left: 2px solid var(--color-border-subtle);
}
</style>
